import { useEffect, useState } from 'react';
import { compensationApi } from '../../../api/compensation.js';

const money = (v, c = 'IDR') => (v == null ? '—' : `${c} ${Number(v).toLocaleString('en-US')}`);

export default function Grades() {
  const [grades, setGrades] = useState([]);
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGrade, setShowGrade] = useState(false);
  const [showBand, setShowBand] = useState(false);
  const [error, setError] = useState(null);

  function refresh() {
    setLoading(true);
    Promise.all([compensationApi.listGrades(), compensationApi.listBands()])
      .then(([g, b]) => {
        setGrades(g?.items || []);
        setBands(b?.items || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function createGrade(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    try {
      await compensationApi.createGrade({
        code: fd.get('code'),
        name: fd.get('name'),
        level: Number(fd.get('level')) || 1,
        description: fd.get('description') || null,
      });
      form.reset();
      setShowGrade(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create grade');
    }
  }

  async function createBand(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    try {
      await compensationApi.createBand({
        gradeId: fd.get('gradeId'),
        currency: fd.get('currency') || 'IDR',
        min: Number(fd.get('min')),
        mid: Number(fd.get('mid')),
        max: Number(fd.get('max')),
      });
      form.reset();
      setShowBand(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create band');
    }
  }

  return (
    <section data-testid="comp-grades-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Grades &amp; Bands</h1>
          <p className="muted">{grades.length} grades · {bands.length} bands</p>
        </div>
        <div className="actions">
          <button className="btn primary" data-testid="comp-grade-create" onClick={() => setShowGrade((s) => !s)}>{showGrade ? 'Close' : '+ Grade'}</button>
          <button className="btn" data-testid="comp-band-create" onClick={() => setShowBand((s) => !s)}>{showBand ? 'Close' : '+ Band'}</button>
        </div>
      </header>

      {error && <div className="alert error" data-testid="comp-grades-error">{error}</div>}

      {showGrade && (
        <form onSubmit={createGrade} className="card form-card grid-form">
          <label>Code<input name="code" data-testid="comp-grade-code-input" required /></label>
          <label>Name<input name="name" data-testid="comp-grade-name-input" required /></label>
          <label>Level<input name="level" type="number" min="1" defaultValue="1" /></label>
          <label className="full">Description<input name="description" /></label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="comp-grade-submit">Create grade</button></div>
        </form>
      )}

      {showBand && (
        <form onSubmit={createBand} className="card form-card grid-form">
          <label>Grade
            <select name="gradeId" data-testid="comp-band-grade-input" required defaultValue="">
              <option value="" disabled>— select —</option>
              {grades.map((g) => <option key={g.id} value={g.id}>{g.code} · {g.name}</option>)}
            </select>
          </label>
          <label>Currency<input name="currency" defaultValue="IDR" /></label>
          <label>Min<input name="min" type="number" data-testid="comp-band-min-input" required /></label>
          <label>Mid<input name="mid" type="number" data-testid="comp-band-mid-input" required /></label>
          <label>Max<input name="max" type="number" data-testid="comp-band-max-input" required /></label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="comp-band-submit">Create band</button></div>
        </form>
      )}

      <div className="card">
        <h3>Grade ladder</h3>
        <table className="data-table" data-testid="comp-grade-list">
          <thead><tr><th>Code</th><th>Name</th><th>Level</th><th>Description</th></tr></thead>
          <tbody>
            {grades.map((g) => (
              <tr key={g.id} data-testid={`comp-grade-row-${g.id}`}>
                <td>{g.code}</td><td>{g.name}</td><td>{g.level}</td><td>{g.description || '—'}</td>
              </tr>
            ))}
            {!loading && grades.length === 0 && <tr><td colSpan={4} className="muted">No grades yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Salary bands</h3>
        <table className="data-table" data-testid="comp-band-list">
          <thead><tr><th>Grade</th><th>Currency</th><th>Min</th><th>Mid</th><th>Max</th></tr></thead>
          <tbody>
            {bands.map((b) => (
              <tr key={b.id} data-testid={`comp-band-row-${b.id}`}>
                <td>{b.grade_code || '—'} · {b.grade_name || ''}</td>
                <td>{b.currency}</td>
                <td>{money(b.min, b.currency)}</td>
                <td>{money(b.mid, b.currency)}</td>
                <td>{money(b.max, b.currency)}</td>
              </tr>
            ))}
            {!loading && bands.length === 0 && <tr><td colSpan={5} className="muted">No bands yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
